/**
 * Format tampilan: uang rupiah, waktu Asia/Jakarta, dan tanggal filter.
 *
 * Semua waktu dari API berupa RFC 3339 dan ditampilkan dalam WIB dengan
 * penanda zona, sementara nilai yang disimpan tetap UTC.
 */

const moneyFormatter = new Intl.NumberFormat('id-ID', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
})

const dateTimeFormatter = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

const jakartaDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Jakarta',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Nominal sen menjadi rupiah bulat dengan pemisah ribuan, misalnya Rp12.500.000. */
export function formatRupiah(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) {
    return 'Rp0'
  }
  const rupiah = Math.trunc(minor / 100)
  const sign = rupiah < 0 ? '-' : ''
  return `${sign}Rp${moneyFormatter.format(Math.abs(rupiah))}`
}

/** Jumlah dengan pemisah ribuan Indonesia. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '0'
  }
  return moneyFormatter.format(value)
}

/** Waktu lengkap dalam WIB, misalnya "29 Sep 2026, 14.05 WIB". */
export function formatDateTimeWib(value: string | null | undefined): string {
  const date = parseInstant(value)
  if (!date) {
    return 'belum ada'
  }
  return `${dateTimeFormatter.format(date)} WIB`
}

/** Tanggal dalam WIB, misalnya "29 Sep 2026". */
export function formatDateWib(value: string | null | undefined): string {
  const date = parseInstant(value)
  if (!date) {
    return 'belum ada'
  }
  return dateFormatter.format(date)
}

function parseInstant(value: string | null | undefined): Date | null {
  if (!value) {
    return null
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Tanggal kalender Asia/Jakarta dari sebuah instant, format YYYY-MM-DD. */
export function jakartaDateOf(value: Date): string {
  return jakartaDateFormatter.format(value)
}

/** Tanggal hari ini di Asia/Jakarta, format YYYY-MM-DD. */
export function todayJakarta(): string {
  return jakartaDateOf(new Date())
}

/** Tanggal N hari sebelum hari ini di Asia/Jakarta, format YYYY-MM-DD. */
export function daysAgoJakarta(days: number): string {
  return jakartaDateOf(new Date(Date.now() - days * 24 * 60 * 60 * 1000))
}

/** Tanggal YYYY-MM-DD yang digeser sejumlah hari, dipakai kalender filter. */
export function shiftDate(value: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) {
    return value
  }
  const base = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return jakartaDateOf(new Date(base + days * 24 * 60 * 60 * 1000))
}

/** Rentang hari inklusif antara dua tanggal YYYY-MM-DD. */
export function inclusiveDayCount(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return 0
  }
  return Math.round((b - a) / (24 * 60 * 60 * 1000)) + 1
}

/** Sisa detik menjadi teks hitung mundur, misalnya "4 menit 12 detik". */
export function formatCountdown(seconds: number): string {
  const total = Math.max(0, Math.ceil(seconds))
  const minutes = Math.floor(total / 60)
  const rest = total % 60
  if (minutes === 0) {
    return `${rest} detik`
  }
  if (rest === 0) {
    return `${minutes} menit`
  }
  return `${minutes} menit ${rest} detik`
}

/** Durasi milidetik menjadi teks singkat. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) {
    return 'tidak dicatat'
  }
  if (ms < 1000) {
    return `${ms} ms`
  }
  return `${(ms / 1000).toFixed(1)} detik`
}

/**
 * Nilai JSON menjadi teks terformat untuk blok kode. Nilai yang berupa string
 * dicoba dibaca sebagai JSON lebih dahulu, karena sebagian kolom menyimpan JSON
 * sebagai teks.
 */
export function prettyJson(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'tidak ada isi'
  }
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      return value
    }
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
